import { CameraIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, useState } from "react";
import { z } from "zod";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUpdateAvatar } from "hooks/useUpdateAvatar";
import { useDeleteAvatarMutation } from "hooks/useUsers";

export const UpdateAvatarSchema = z.object({
    file: z.instanceof(File),
});

export type UpdateAvatar = z.infer<typeof UpdateAvatarSchema>;

export const ProfilePictureUpload = ({
    email,
    avatar,
    name,
}: {
    email: string | undefined;
    avatar: string | undefined;
    name?: string | undefined;
}) => {
    const currentUser = useCurrentUser();
    const { enqueueSnackbar } = useSnackbar();
    const updateAvatar = useUpdateAvatar();

    const [tmpDisplay, setTmpDisplay] = useState(avatar);
    const [isLoading, setIsLoading] = useState(false);

    const deleteAvatarMutation = useDeleteAvatarMutation();
    const isDeleting = deleteAvatarMutation.isPending;

    const handleDeleteAvatar = async () => {
        try {
            await deleteAvatarMutation.mutateAsync();
            setTmpDisplay(undefined);
            enqueueSnackbar("Avatar deleted successfully.", { variant: "success" });
        } catch (error) {
            console.error("Error deleting avatar:", error);
        }
    };

    const handleProfilePicUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) {
            enqueueSnackbar("Please upload a file.", { variant: "warning" });
            return;
        }

        const acceptedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!acceptedTypes.includes(file.type)) {
            enqueueSnackbar("Only .jpeg, .jpg, and .png files are allowed.", {
                variant: "error",
            });
            e.target.value = "";
            return;
        }

        try {
            setIsLoading(true);
            setTmpDisplay(URL.createObjectURL(file));
            await updateAvatar({ file });
            enqueueSnackbar("Profile picture updated successfully.", { variant: "success" });
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Error updating profile picture.", { variant: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const displayAvatar = tmpDisplay || avatar;

    return (
        <div className="flex flex-col items-center justify-center rounded-2xl border-none bg-[#2da8e0] p-8 shadow-none">
            <div className="text-center">
                <>
                    <input
                        type="file"
                        id="profilePic"
                        accept=".jpeg, .jpg, .png"
                        disabled={isLoading || isDeleting}
                        onChange={(e) => handleProfilePicUpload(e)}
                        className="hidden"
                    />
                    <div
                        className="relative flex h-32 w-32 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-black bg-opacity-50 hover:border-gray-600"
                        onClick={() => document.getElementById("profilePic")?.click()}
                    >
                        {displayAvatar ? (
                            <img
                                src={displayAvatar}
                                alt="Profile"
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                        ) : null}

                        {/* Camera Icon and Upload Text */}
                        <div className="absolute inset-0 bg-black bg-opacity-50 text-white">
                            {isLoading ? (
                                <div className="flex h-full flex-col items-center justify-center">
                                    Loading...
                                </div>
                            ) : (
                                <div className="flex h-full flex-col items-center justify-center">
                                    <CameraIcon className="h-6 w-6" />
                                    <span className="mt-1 text-sm">Upload photo</span>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            </div>
            <div className="mt-4 text-sm text-white">Allowed *.jpeg, *.jpg, *.png</div>
            <div className="mt-4 font-medium text-white">{name}</div>
            <div className="mt-4 font-medium text-white">{email}</div>

            {currentUser?.avatarUrl && (
                <button
                    onClick={handleDeleteAvatar}
                    disabled={isDeleting}
                    className="mt-4 flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isDeleting ? (
                        <>
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            Deleting...
                        </>
                    ) : (
                        <>
                            <TrashIcon className="mr-2 h-4 w-4" />
                            Delete Photo
                        </>
                    )}
                </button>
            )}
        </div>
    );
};
